import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { ComparatorOperators, ExportUsersGQL, ExportUsersMutation, GetUserLegalEntitiesGQL, GetUserLegalEntitiesQuery, GetUsersFilterNamesGQL, GetUsersFilterNamesQuery, GetUsersGQL, GetUsersQuery, OperatorInput, OrderTypeInput, PagingInput, UpdateUserLegalEntityPermissionLevelGQL, UpdateUserLegalEntityPermissionLevelMutation, UserFields, UserQueryComplexInput, UserQuerySimpleInput, UserSimpleQueryInput, UserSort } from "../../../graphql/generated";
import { LazyLoadParameters } from "../features/administration/legal-entities/legal-entities.component";
import { USERS_FIELD } from "../shared/graphql-operations/user/users.enum";

/** Page size used to collect every distinct value shown in the users table filters */
const FILTER_NAMES_PAGE_SIZE = 10000000;

/** Fields matched by the global search of the users table */
const USERS_SEARCH_FIELDS: UserFields[] = [
    UserFields.BusinessUnit,
    UserFields.GaltMemberReference,
    UserFields.LegalEntity,
    UserFields.OrganizationalUnit,
    UserFields.Status,
    UserFields.TypeOfContact,
    UserFields.UserAccountsEmail,
    UserFields.UserAccountsName,
    UserFields.UserRoleGalt,
    UserFields.UserRoleAuditSelfAssesment
];

@Injectable({
    providedIn: 'root'
})

export class UserService {
    private readonly ExportUsersGQL: ExportUsersGQL = inject(ExportUsersGQL);
    private readonly GetUsersGQL: GetUsersGQL = inject(GetUsersGQL);
    private readonly GetUsersFilterNamesGQL: GetUsersFilterNamesGQL = inject(GetUsersFilterNamesGQL);
    private readonly GetUserLegalEntitiesGQL: GetUserLegalEntitiesGQL = inject(GetUserLegalEntitiesGQL);
    private readonly UpdateUserLegalEntityPermissionLevelGQL: UpdateUserLegalEntityPermissionLevelGQL = inject(UpdateUserLegalEntityPermissionLevelGQL);

    /**
     * Loads a page of the users table. The legal entities of every user are not part of the
     * response: they are loaded on row expansion by {@link getUserLegalEntities}.
     */
    getUsers(parameters: LazyLoadParameters): Observable<GetUsersQuery['usersAndLegalEntityByQueryComplex']> {
        const { query, page, sort } = convertLazyLoadToUsersQuery(parameters);
        return this.GetUsersGQL.fetch({ query, page, sort }, { fetchPolicy: 'no-cache' })
            .pipe(map(res => res.data.usersAndLegalEntityByQueryComplex));
    }

    /** Loads the distinct values used to fill the users table filters */
    getUsersFilterNames(): Observable<GetUsersFilterNamesQuery['usersAndLegalEntityByQueryComplex']> {
        return this.GetUsersFilterNamesGQL.fetch({
            page: { pageNo: 0, pageSize: FILTER_NAMES_PAGE_SIZE },
            sort: { sortField: UserFields.UserAccountsName, sortOrder: OrderTypeInput.Asc }
        }, { fetchPolicy: 'no-cache' })
            .pipe(map(res => res.data.usersAndLegalEntityByQueryComplex));
    }

    /** Loads the legal entities of a single user, shown in the expanded row of the users table */
    getUserLegalEntities(userId: string): Observable<GetUserLegalEntitiesQuery['userLegalEntitiesByUserId']> {
        return this.GetUserLegalEntitiesGQL.fetch({ userId }, { fetchPolicy: 'no-cache' })
            .pipe(map(res => res.data.userLegalEntitiesByUserId));
    }

    updateUserPermission(userId: string, legalEntityId: string, permissionLevel: string): Observable<UpdateUserLegalEntityPermissionLevelMutation['updateUserLegalEntityPermissionLevel']> {
        return this.UpdateUserLegalEntityPermissionLevelGQL.mutate({ userId, legalEntityId, permissionLevel }, { fetchPolicy: 'no-cache' })
            .pipe(map(res => res.data?.updateUserLegalEntityPermissionLevel));
    }

    exportUsers(parameters: any, timeZone: string): Observable<ExportUsersMutation['downloadUsersAndLegalEntityByQuery']> {
        const param = convertLazyLoadToComplexWithSortAndPage(parameters);
        return this.ExportUsersGQL.mutate({ query: param.query as UserQueryComplexInput, sort: param.sort as UserSort, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(result => result.data?.downloadUsersAndLegalEntityByQuery));
    }
}

/**
 * Converts the lazy load event of the users table into the query, page and sort inputs of
 * usersAndLegalEntityByQueryComplex: the column filters are matched, the global search is
 * applied in OR over {@link USERS_SEARCH_FIELDS}.
 */
export function convertLazyLoadToUsersQuery(parameters: LazyLoadParameters): { query: UserQueryComplexInput, page: PagingInput, sort: UserSort } {
    const filters: UserSimpleQueryInput[] = [];

    Object.entries(parameters?.filters ?? {}).forEach(([field, values]) => {
        const userField: UserFields | undefined = USERS_FIELD[field];
        if (userField && Array.isArray(values) && values.length > 0) {
            filters.push({
                field: userField,
                filters: {
                    operator: ComparatorOperators.Match,
                    value: values.map(value => value?.toString() ?? '')
                }
            });
        }
    });

    const search: UserQuerySimpleInput = {
        operator: OperatorInput.Or,
        query: parameters?.search
            ? USERS_SEARCH_FIELDS.map(field => ({
                field,
                filters: {
                    operator: ComparatorOperators.Contains,
                    value: [parameters.search as string]
                }
            }))
            : []
    };

    return {
        query: {
            operator: OperatorInput.And,
            query: [{ query: filters }, search]
        },
        page: { pageNo: parameters?.page, pageSize: parameters?.rows },
        sort: {
            sortField: USERS_FIELD[parameters?.order?.sortField ?? 'userAccountsName'] ?? UserFields.UserAccountsName,
            sortOrder: parameters?.order?.sortDirection === 'DESC' ? OrderTypeInput.Desc : OrderTypeInput.Asc
        }
    };
}

export function convertLazyLoadToComplexWithSortAndPage(parameters: any): { query: UserQueryComplexInput, page: PagingInput, sort: UserSort } {
    const query: UserSimpleQueryInput[] = [];
    if (parameters) {

        Object.entries(parameters?.filters).forEach(([field, values]) => {
            if (values !== null && Array.isArray(values) && values.length > 0) {
                (ENUM_USER[field as keyof typeof ENUM_USER] || [field]).forEach((key) => {
                    query.push({
                        field: key as UserFields,
                        filters: {
                            operator: ComparatorOperators.Match,
                            value: values
                        }
                    });
                })
            }
        });
    }

    const search: UserQuerySimpleInput = {
        operator: OperatorInput.Or,
        query: [] as UserSimpleQueryInput[]
    }

    if (parameters?.search) {
        [
            "businessUnit",
            "galtMemberReference",
            "legalEntity",
            "newUserAccountsFlag",
            "organizationalUnit",
            "status",
            "typeOfContact",
            "userAccountsEmail",
            "userAccountsId",
            "userAccountsName",
            "userRoleGalt",
            "userRoleLocalChief",
            "userRoleOther"
        ].forEach((field) => {
            (ENUM_USER[field as keyof typeof ENUM_USER] || [field]).forEach((key) => {
                search.query?.push({
                    field: key as UserFields,
                    filters: {
                        operator: ComparatorOperators.Contains,
                        value: parameters.search
                    }
                });
            })
        });
    }

    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query

                },

                search
            ]
        },
        sort: {
            sortField: ENUM_USER?.[parameters?.order?.sortField as keyof typeof ENUM_USER]?.[0] as UserFields || "USER_ACCOUNTS_NAME",
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc || OrderTypeInput.Asc
        },
        page: {
            pageNo: parameters?.pa,
            pageSize: parameters?.p
        }
    }
}


export const ENUM_USER = {
    businessUnit: ['BUSINESS_UNIT'],
    galtMemberReference: ['GALT_MEMBER_REFERENCE'],
    legalEntity: ['LEGAL_ENTITY'],
    newUserAccountsFlag: ['NEW_USER_ACCOUNTS_FLAG'],
    organizationalUnit: ['ORGANIZATIONAL_UNIT'],
    status: ['STATUS'],
    typeOfContact: ['TYPE_OF_CONTACT'],
    userAccountsEmail: ['USER_ACCOUNTS_EMAIL'],
    userAccountsId: ['USER_ACCOUNTS_ID'],
    userAccountsName: ['USER_ACCOUNTS_NAME'],
    userRoleGalt: ['USER_ROLE_GALT'],
    userRoleLocalChief: ['USER_ROLE_LOCAL_CHIEF'],
    userRoleOther: ['USER_ROLE_OTHER']
}
